<script lang="ts" setup>
import { ref, watch } from "vue";

const props = defineProps({
  modelValue: Boolean,
  disabled: Boolean,
  readOnly: Boolean,
  label: {
    type: String,
    required: false,
  },
  // add type prop of string that can be either a checkbox or radio
  type: {
    type: String as PropType<"checkbox" | "radio">,
    default: "checkbox",
  },
  size: {
    type: String as PropType<"small" | "input" | "medium" | "large">,
    default: "medium",
  },
});
const emit = defineEmits(["update:modelValue"]);

const isChecked = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newVal) => {
    isChecked.value = newVal;
  }
);

watch(isChecked, (newVal) => {
  emit("update:modelValue", newVal);
});

const toggle = () => {
  if (!props.disabled && !props.readOnly) {
    isChecked.value = !isChecked.value;
  }
};

const checkSize = computed(() => {
  switch (props.size) {
    case "small":
      return 14;
    case "input":
      return 18;
    case "medium":
      return 20;
    case "large":
      return 24;
    default:
      return 20;
  }
});
</script>

<template>
  <div
    class="flex flex-row items-center cursor-pointer select-none"
    @click="toggle"
    :class="{
      'text-body-2': props.size == 'small',
      'text-body': props.size == 'medium' || props.size == 'large',
    }"
  >
    <div
      :class="{
        checkbox: true,
        checked: isChecked,
        ' bg-primary-default border-primary-default': isChecked,
        'border-medium hover:bg-light bg-surface': !isChecked,
        rounded: props.type == 'checkbox',
        'rounded-full': props.type == 'radio',

        'w-[14px] h-[14px] border-[1px] rounded-[4px] relative pointer-events-none text-[14px]':
          props.size == 'small',
        'w-[16px] h-[16px] border-[1px] relative pointer-events-none text-body-2':
          props.size == 'input',
        'w-[18px] h-[18px] border-[1px] relative pointer-events-none text-body':
          props.size == 'medium',
        'w-[24px] h-[24px] border-[1px] relative pointer-events-none text-body':
          props.size == 'large',
      }"
    >
      <icon
        v-if="isChecked"
        name="pajamas:check"
        class="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] text-success-on"
        :size="`${checkSize}`"
      />
    </div>
    <span v-if="label" class="ml-1">{{ label }}</span>
  </div>
</template>

<style scoped>
.checkbox {
  display: inline-block;

  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.checkbox.checked {
  animation: bounce 0.3s ease;
}

.checkbox.checked .checkmark {
  display: block;
  transform: scale(1);
}

.checkmark {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 50%;
  height: 50%;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: translate(-50%, -50%) rotate(45deg) scale(0);
  transition: transform 0.3s ease;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: scale(1);
  }
  40% {
    transform: scale(1.1);
  }
  60% {
    transform: scale(0.9);
  }
}
</style>
